// Copyright (c) 2024-2026 Echonum LLC. All rights reserved.
// Product: Exploit Score ETS (Exploit Threat Score)
// Version: 2.8.6
//
// This file is part of Exploit Score ETS, proprietary software developed by
// Echonum LLC. Unauthorized copying, distribution, modification, or use of
// this file, via any medium, is strictly prohibited without express written
// permission from Echonum LLC.
//
// Confidential and Proprietary.
//
// In-app replacement for the browser's native alert()/confirm()/prompt().
// Mount <DialogHost /> once at the app root; call confirmDialog()/promptDialog()/notify()
// from anywhere (including outside the React tree, e.g. axios interceptors).

import React, { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, CheckCircle, Info, XCircle, X } from 'lucide-react';

export interface ConfirmOpts {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  danger?: boolean;
}
export interface PromptOpts extends ConfirmOpts {
  placeholder?: string;
  defaultValue?: string;
  inputType?: string;
}
export type ToastType = 'success' | 'error' | 'info';

// --- Imperative API (handlers registered by the mounted host) ---
let _confirm: ((o: ConfirmOpts) => Promise<boolean>) | null = null;
let _prompt: ((o: PromptOpts) => Promise<string | null>) | null = null;
let _toast: ((msg: string, type: ToastType) => void) | null = null;

export function confirmDialog(opts: ConfirmOpts): Promise<boolean> {
  if (_confirm) return _confirm(opts);
  return Promise.resolve(window.confirm(opts.message)); // graceful fallback if host not mounted
}
export function promptDialog(opts: PromptOpts): Promise<string | null> {
  if (_prompt) return _prompt(opts);
  return Promise.resolve(window.prompt(opts.message, opts.defaultValue || ''));
}
export function notify(message: string, type: ToastType = 'info') {
  if (_toast) _toast(message, type);
}

interface ConfirmState { opts: ConfirmOpts; resolve: (v: boolean) => void; }
interface PromptState { opts: PromptOpts; resolve: (v: string | null) => void; }
interface ToastItem { id: number; message: string; type: ToastType; }

export const DialogHost: React.FC = () => {
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const [promptState, setPromptState] = useState<PromptState | null>(null);
  const [promptValue, setPromptValue] = useState('');
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const pushToast = useCallback((message: string, type: ToastType) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, message, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4500);
  }, []);

  useEffect(() => {
    _confirm = (opts) => new Promise<boolean>((resolve) => setConfirmState({ opts, resolve }));
    _prompt = (opts) =>
      new Promise<string | null>((resolve) => {
        setPromptValue(opts.defaultValue || '');
        setPromptState({ opts, resolve });
      });
    _toast = pushToast;
    return () => { _confirm = null; _prompt = null; _toast = null; };
  }, [pushToast]);

  const closeConfirm = (v: boolean) => {
    confirmState?.resolve(v);
    setConfirmState(null);
  };
  const closePrompt = (v: string | null) => {
    promptState?.resolve(v);
    setPromptState(null);
  };

  const ToastIcon = ({ type }: { type: ToastType }) =>
    type === 'success' ? <CheckCircle size={18} className="text-green-400" />
      : type === 'error' ? <XCircle size={18} className="text-red-400" />
      : <Info size={18} className="text-blue-400" />;

  return (
    <>
      {/* Confirm dialog */}
      {confirmState && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-start gap-3 mb-4">
              {confirmState.opts.danger && <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />}
              <div>
                <h3 className="text-lg font-bold text-gray-900">{confirmState.opts.title || 'Please confirm'}</h3>
                <p className="text-sm text-gray-600 mt-1">{confirmState.opts.message}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => closeConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                {confirmState.opts.cancelText || 'Cancel'}
              </button>
              <button
                onClick={() => closeConfirm(true)}
                className={`flex-1 px-4 py-2 text-white rounded-lg ${
                  confirmState.opts.danger ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {confirmState.opts.confirmText || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Prompt dialog */}
      {promptState && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-1">{promptState.opts.title || 'Enter value'}</h3>
            <p className="text-sm text-gray-600 mb-4">{promptState.opts.message}</p>
            <input
              autoFocus
              type={promptState.opts.inputType || 'text'}
              value={promptValue}
              placeholder={promptState.opts.placeholder}
              onChange={(e) => setPromptValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') closePrompt(promptValue); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => closePrompt(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                {promptState.opts.cancelText || 'Cancel'}
              </button>
              <button
                onClick={() => closePrompt(promptValue)}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {promptState.opts.confirmText || 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toasts */}
      <div className="fixed top-4 right-4 z-[110] flex flex-col gap-2 w-80 max-w-[calc(100vw-2rem)]">
        {toasts.map((t) => (
          <div
            key={t.id}
            className="flex items-start gap-2 bg-slate-800 text-slate-100 border border-slate-600 rounded-lg px-4 py-3 shadow-lg"
          >
            <ToastIcon type={t.type} />
            <span className="text-sm flex-1">{t.message}</span>
            <button onClick={() => setToasts((x) => x.filter((i) => i.id !== t.id))} className="text-slate-400 hover:text-slate-200">
              <X size={16} />
            </button>
          </div>
        ))}
      </div>
    </>
  );
};

export default DialogHost;
